import React, { useCallback, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import type { Task, TaskStatusEnum } from '@ztechapps/common.contract'
import { TaskStatus } from '@ztechapps/common.contract'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { HorizontalStack, Icon, resolveStatusColor, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import type { GroupedSection } from '@ztechapps/task.common'
import { buildTaskGroups, TaskBoardGroupByEnum } from '@ztechapps/task.common'
import { useTaskFilterBarContext } from '../TaskFilterBar/Context'
import { TaskInput } from '../TaskInput'
import { useQuickAddTask } from '../QuickAddTask'
import { useTaskBoardContext } from './Context'
import { TaskBoardCard } from './TaskBoardCard'
import styles from './TaskBoard.module.scss'

type TaskBoardColumnProps = {
  status: TaskStatusEnum
  tasks: Task[]
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
  groupBy?: TaskBoardGroupByEnum
}

const statusConfigMap = Object.values(TaskStatus).reduce(
  (acc, s) => {
    acc[s.value] = s
    return acc
  },
  {} as Record<string, (typeof TaskStatus)[keyof typeof TaskStatus]>
)

type DraggableCardProps = {
  task: Task
  status: TaskStatusEnum
  isTransitioning: boolean
  groupKey?: string
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
}

const LOCKED_TOOLTIP = 'Project is completed or cancelled. Status cannot be changed.'

const DraggableCard = ({ task, status, isTransitioning, groupKey, onDoubleClick }: DraggableCardProps) => {
  const { canEdit } = useTaskBoardContext()
  const [isDragging, setIsDragging] = useState(false)

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.dataTransfer.setData('taskId', task._id)
      e.dataTransfer.setData('fromStatus', status)
      if (groupKey) e.dataTransfer.setData('groupKey', groupKey)
      e.dataTransfer.effectAllowed = 'move'
      setIsDragging(true)
    },
    [task._id, status, groupKey]
  )

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  if (!canEdit) {
    return (
      <TooltipPopOver tooltipBody={LOCKED_TOOLTIP}>
        <div className={styles.cardLocked}>
          <TaskBoardCard task={task} isTransitioning={isTransitioning} onDoubleClick={onDoubleClick} />
        </div>
      </TooltipPopOver>
    )
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`${styles.cardDrag} ${isDragging ? styles.cardDragging : ''}`}
    >
      <TaskBoardCard task={task} isTransitioning={isTransitioning} onDoubleClick={onDoubleClick} />
    </div>
  )
}

type CardListProps = {
  tasks: Task[]
  status: TaskStatusEnum
  groupKey?: string
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
}

const CardList = ({ tasks, status, groupKey, onDoubleClick }: CardListProps) => {
  const { transitioningTaskIds } = useTaskBoardContext()

  return (
    <>
      {tasks.map((task) => (
        <DraggableCard
          key={task._id}
          task={task}
          status={status}
          groupKey={groupKey}
          isTransitioning={transitioningTaskIds.has(task._id)}
          onDoubleClick={onDoubleClick}
        />
      ))}
    </>
  )
}

type GroupSectionProps = {
  group: GroupedSection
  status: TaskStatusEnum
  collapsed: boolean
  onToggle: () => void
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
  groupBy: TaskBoardGroupByEnum
}

const GroupSection = ({ group, status, collapsed, onToggle, onDoubleClick, groupBy }: GroupSectionProps) => {
  const { canEdit } = useTaskBoardContext()
  const [showInput, setShowInput] = useState(false)
  const { onSubmit } = useQuickAddTask()
  const isProjectGroup = groupBy === TaskBoardGroupByEnum.Project
  const projectId = isProjectGroup ? group.tasks[0]?.project?._id : undefined

  const handleAdd = async (taskName: string) => {
    await onSubmit({
      name: taskName,
      project: projectId ? { _id: projectId } : null,
      status,
    })
    setShowInput(false)
  }

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowInput((prev) => !prev)
  }

  return (
    <div className={`${styles.groupSection} border-bottom`}>
      <HorizontalStack className={`${styles.groupHeader} rounded-1 justify-content-between`} onClick={onToggle}>
        <HorizontalStack gap={2}>
          <small className={`${styles.groupChevron} text-secondary ${collapsed ? styles.groupChevronCollapsed : ''}`}>
            <Icon name="chevron-down" />
          </small>
          <div className="fw-semibold text-body flex-grow-1">{group.label}</div>

          <div>
            <ShowIf condition={isProjectGroup && canEdit}>
              <BusyButtonLink icon="plus" text="Add task" onClick={handleAddClick} />
            </ShowIf>
          </div>
        </HorizontalStack>
        <div className="px-2">
          <small className="fw-semibold text-secondary">{group.tasks.length}</small>
        </div>
      </HorizontalStack>
      <ShowIf condition={isProjectGroup && canEdit && showInput}>
        <div className="p-2">
          <TaskInput onAdd={handleAdd} placeholder="Enter task name and press Enter" />
        </div>
      </ShowIf>
      <ShowIf condition={!collapsed}>
        <CardList tasks={group.tasks} status={status} groupKey={group.key} onDoubleClick={onDoubleClick} />
      </ShowIf>
    </div>
  )
}

export const TaskBoardColumn = ({ status, tasks, onDoubleClick, groupBy: groupByProp }: TaskBoardColumnProps) => {
  const { groupBy: groupByContext } = useTaskFilterBarContext()
  const groupByValue = groupByProp ?? groupByContext ?? TaskBoardGroupByEnum.None
  const { onStatusChange, collapsedGroups, toggleGroup, expandGroup, canEdit } = useTaskBoardContext()
  const statusConfig = statusConfigMap[status]
  const [isDragOver, setIsDragOver] = useState(false)

  const groups = useMemo(() => buildTaskGroups(tasks, groupByValue), [tasks, groupByValue])
  const isGrouped = groupByValue !== TaskBoardGroupByEnum.None

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!canEdit) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    },
    [canEdit]
  )

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!canEdit) return
      e.preventDefault()
      setIsDragOver(true)
    },
    [canEdit]
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!canEdit) return
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDragOver(false)
      }
    },
    [canEdit]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!canEdit) return
      e.preventDefault()
      setIsDragOver(false)
      const taskId = e.dataTransfer.getData('taskId')
      const fromStatus = e.dataTransfer.getData('fromStatus') as TaskStatusEnum
      const groupKey = e.dataTransfer.getData('groupKey')
      if (taskId && fromStatus !== status) {
        // Expand the target group so the dropped card is visible
        if (groupKey && isGrouped) {
          expandGroup(groupKey)
        }
        onStatusChange(taskId, fromStatus, status)
      }
    },
    [canEdit, onStatusChange, status, isGrouped, expandGroup]
  )

  return (
    <div className={`${styles.column} d-flex flex-column bg-body-secondary border rounded-2 overflow-hidden`}>
      <HorizontalStack gap={2} className="px-3 py-2 border-bottom bg-body">
        <span
          className={`${styles.columnDot} flex-shrink-0`}
          style={{ backgroundColor: resolveStatusColor(statusConfig)?.fill }}
        />
        <span className="fw-semibold text-body flex-grow-1">{status}</span>
        <small className={`${styles.columnCount} fw-semibold text-secondary bg-body-secondary rounded-pill`}>
          {tasks.length}
        </small>
      </HorizontalStack>
      <div
        className={`${styles.columnBody} ${isDragOver ? styles.columnBodyDragOver : ''} flex-fill overflow-auto p-2 d-flex flex-column`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <ShowIf condition={isEmpty(tasks)}>
          <div className={styles.dropZone} />
          <HorizontalStack className="justify-content-center p-3 text-secondary fst-italic">No tasks</HorizontalStack>
        </ShowIf>
        <ShowIf condition={!isEmpty(tasks) && !isGrouped}>
          <CardList tasks={tasks} status={status} onDoubleClick={onDoubleClick} />
        </ShowIf>
        <ShowIf condition={!isEmpty(tasks) && isGrouped}>
          {groups.map((group) => (
            <GroupSection
              key={group.key}
              group={group}
              status={status}
              collapsed={!!collapsedGroups[group.key]}
              onToggle={() => toggleGroup(group.key)}
              onDoubleClick={onDoubleClick}
              groupBy={groupByValue}
            />
          ))}
        </ShowIf>
      </div>
    </div>
  )
}
